/* eslint-disable indent */
import { createAction as c } from 'redux-actions';
import { bind } from 'redux-effects';
import { localstorage } from 'optilit-frontend-helpers';
import { track as trackingTypes } from 'analytics_types';
import { track, location } from 'optilit-action-creators';
import { sleep, api } from '../util';
import { getUserID, getAuthToken } from '../selector';
import * as t from './types';

// ---
// Trivial actions.
// ---
export const filterArticles = c(t.TRACK_FILTER_ARTICLES);
export const initSocket = c(t.INIT_SOCKET);
export const detailClick = c(t.TRACK_DETAIL_CLICK);
export const flushOrder = c(t.FLUSH_ORDER);
export const updateCartItemRequest = c(t.UPDATE_CART_ITEM_REQUEST);
export const updateCartItemSuccess = c(t.UPDATE_CART_ITEM_SUCCESS);
export const updateCartItemFailure = c(t.UPDATE_CART_ITEM_FAILURE);
export const resetCartItemError = c(t.RESET_CART_ITEM_ERROR);
export const authenticationRequest = c(t.AUTHENTICATION_REQUEST);
export const authenticationRequestFailure = c(t.AUTHENTICATION_REQUEST_FAILURE);
export const authenticationRequestSuccess = c(t.AUTHENTICATION_REQUEST_SUCCESS);
export const logoutRequest = c(t.LOGOUT_REQUEST);
export const logoutRequestSuccess = c(t.LOGOUT_REQUEST_SUCCESS);
export const logoutRequestFailure = c(t.LOGOUT_REQUEST_FAILURE);
export const cartRequest = c(t.CART_REQUEST);
export const cartRequestSuccess = c(t.CART_REQUEST_SUCCESS);
export const cartRequestFailure = c(t.CART_REQUEST_FAILURE);
export const setCart = c(t.SET_CART);
export const setCurrentUser = c(t.SET_CURRENT_USER);
export const clearCurrentUser = c(t.CLEAR_CURRENT_USER);
export const profileRequest = c(t.PROFILE_REQUEST);
export const setCurrentProfile = c(t.SET_CURRENT_PROFILE);
export const profileRequestSuccess = c(t.PROFILE_REQUEST_SUCCESS);
export const profileRequestFailure = c(t.PROFILE_REQUEST_FAILURE);
export const createOrderRequest = c(t.CREATE_ORDER_REQUEST);
export const createOrderSuccess = c(t.CREATE_ORDER_SUCCESS);
export const createOrderFailure = c(t.CREATE_ORDER_FAILURE);
export const payOrderRequest = c(t.PAY_ORDER_REQUEST);
export const payOrderSuccess = c(t.PAY_ORDER_SUCCESS);
export const payOrderFailure = c(t.PAY_ORDER_FAILURE);
export const setPaymentMethod = c(t.SET_PAYMENT_METHOD);
export const setActiveOrder = c(t.SET_ACTIVE_ORDER);
export const updateActiveOrder = c(t.UPDATE_ACTIVE_ORDER);
export const clearActiveOrder = c(t.CLEAR_ACTIVE_ORDER);
export const toggleNav = c(t.TOGGLE_NAV);
export const toggleCart = c(t.TOGGLE_CART);
export const toggleFilter = c(t.TOGGLE_FILTER);
export const togglePrices = c(t.TOGGLE_PRICES);
export const toggleOrderSummary = c(t.TOGGLE_ORDER_SUMMARY);
export const toggleArticleDetail = c(t.TOGGLE_ARTICLE_DETAIL);
export const setOrderWizardValues = c(t.SET_ORDER_WIZARD_VALUES);
export const clearOrderWizardValues = c(t.CLEAR_ORDER_WIZARD_VALUES);
export const createStripeSource = c(t.CREATE_STRIPE_SOURCE);
export const createStripeSourceRequest = c(t.CREATE_STRIPE_SOURCE_REQUEST);
export const createStripeSourceSuccess = c(t.CREATE_STRIPE_SOURCE_SUCCESS);
export const createStripeSourceFailure = c(t.CREATE_STRIPE_SOURCE_FAILURE);
export const changeLocation = c(t.LOCATION_CHANGE);
export const orderStatusRequest = c(t.ORDER_STATUS_REQUEST);
export const orderStatusSuccess = c(t.ORDER_STATUS_SUCCESS);
export const orderStatusFailure = c(t.ORDER_STATUS_FAILURE);
export const apiCartByUserID = data => api('cartByUserID', data);
export const apiProfile = data => api('profile', data);
export const apiLogin = data => api('login', data);
export const apiVerify = () => api('verify');
export const apiUpdateCartItem = data => api('updateCartItem', data);
export const apiDeleteCartItem = data => api('deleteCartItem', data);
export const apiAddCartItem = data => api('addCartItem', data);
export const apiCreateOrder = data => api('order', data);
export const apiOrderStatus = data => api('orderStatus', data);
export const apiPayOrder = data => api('payOrder', data);
export const apiLogout = data => api('logout', data);
export const submitOrderWizard = payload => dispatch => {
  const { values, history } = payload;
  dispatch(setOrderWizardValues(values));
  history.push('/checkout/payorder');
};

export const setActivDetailItem = payload => dispatch => {
  dispatch(toggleArticleDetail(payload));
};
export const trackArticleDetailClick = articleID =>
  track(trackingTypes.ARTICLE_DETAIL_CLICK, detailClick({ articleID }));
export const trackFilterArticles = payload =>
  track(trackingTypes.FILTER_ARTICLES, filterArticles(payload));

// ---
// Non-trivial actions.
// ---
export const fetchOrderStatus = ({ order_id }) => dispatch => {
  let count = 0;
  const poll = () => {
    if (count === 0) dispatch(orderStatusRequest());
    dispatch(
      bind(
        apiOrderStatus({ order_id }),
        ({ order_status }) => {
          if (order_status !== 'pending') {
            if (order_status === 'paid') {
              dispatch(orderStatusSuccess());
              dispatch(payOrderSuccess());
            } else {
              dispatch(orderStatusSuccess());
              dispatch(
                payOrderFailure({
                  message:
                    'Leider konnten wir Ihre Zahlung nicht verarbeiten, bitte verwenden Sie eine andere Zahlungsmethode. Bei Fragen wenden Sie sich an unsere Auftragsbearbeitung.',
                })
              );
            }
          } else {
            count = count + 1;
            setTimeout(() => poll(), 3000);
          }
        },
        err => {
          dispatch(orderStatusFailure(err));
        }
      )
    );
  };

  return poll();
};

// ---
// Cart.
// ---
export const updateCartItem = (method, data) => dispatch => {
  let call;

  switch (method) {
    case 'update':
      call = apiUpdateCartItem;
      break;
    case 'delete':
      call = apiDeleteCartItem;
      break;
    case 'add':
      call = apiAddCartItem;
      break;
    default:
      throw new Error('Unkown cart api method');
  }

  const { articleID } = data;
  dispatch(updateCartItemRequest({ articleID }));

  return dispatch(
    bind(
      call(data),
      ({ id, items, metadata, summary }) => {
        dispatch(updateCartItemSuccess({ articleID }));
        dispatch(setCart({ id, items, metadata, summary }));
      },
      () => {
        dispatch(updateCartItemFailure({ articleID }));
        dispatch(
          (async () => {
            await sleep(2000);
            return resetCartItemError({ articleID: data.articleID });
          })()
        );
      }
    )
  );
};

export const cart = ({ id }) => dispatch => {
  dispatch(cartRequest());

  return dispatch(
    bind(
      apiCartByUserID({ id }),
      ({ id, items, metadata, summary }) => {
        dispatch(setCart({ id, items, metadata, summary }));
        dispatch(cartRequestSuccess());
      },
      err => {
        dispatch(cartRequestFailure(err));
      }
    )
  );
};

export const profile = id => dispatch => {
  dispatch(profileRequest());

  return dispatch(
    bind(
      apiProfile({ id }),
      data => {
        // TODO: order matters
        dispatch(setCurrentProfile(data));
        dispatch(profileRequestSuccess());
      },
      err => {
        dispatch(profileRequestFailure(err));
      }
    )
  );
};

export const authenticate = (method, data) => dispatch => {
  dispatch(authenticationRequest());

  let apiMethod;
  if (method === 'login') {
    const { user } = data;
    apiMethod = apiLogin(user);
  }

  if (method === 'verification') {
    apiMethod = apiVerify();
  }

  if (!apiMethod)
    throw new Error(
      'please provide a valid api method: either \'login\' or \'verification\''
    );

  return dispatch(
    bind(
      apiMethod,
      ({ token, id, role }) => {
        localstorage.storeItem('auth_token', token);
        dispatch(authenticationRequestSuccess());
        dispatch(
          setCurrentUser({
            id,
            role,
            token,
            dateOfAuthentication: new Date().getMilliseconds(),
          })
        );
        dispatch(initSocket({ id, token }));
      },
      err => {
        dispatch(authenticationRequestFailure(err));
        return Promise.reject(err);
      }
    )
  );
};

export const onAuthentication = (method, history, data) => (
  dispatch,
  getState
) => {
  return dispatch(authenticate(method, data))
    .then(() => {
      const id = getUserID(getState());
      return Promise.all([dispatch(profile(id)), dispatch(cart({ id }))]).then(
        () => {
          if (method === 'login') history.push('/');
        }
      );
    })
    .catch(err => {
      if (method === 'verification') history.push('/login');
      // eslint-disable-next-line
      console.error(err);
    });
};

export const logout = () => (dispatch, getState) => {
  dispatch(logoutRequest());
  const token = getAuthToken(getState());

  return dispatch(
    bind(
      apiLogout({ token }),
      () => {
        dispatch(logoutRequestSuccess());
        dispatch(changeLocation('/'));
      },
      err => dispatch(logoutRequestFailure(err))
    )
  );
};

export const invalidateOrder = () => (dispatch, getState) => {
  dispatch(flushOrder());

  const id = getUserID(getState());
  return dispatch(cart({ id }));
};

export const createOrder = ({ stripe, payment, history }) => (
  dispatch,
  getState
) => {
  const { data } = getState();
  const { cart, order } = data;

  dispatch(setPaymentMethod({ method: payment.type }));
  dispatch(createOrderRequest());

  return dispatch(
    bind(
      apiCreateOrder({
        cart_id: cart.id,
        ...order.orderForm,
      }),
      order => {
        const { order_status, order_id, customer } = order;
        dispatch(setActiveOrder({ order_id, order_status }));

        if (order_status === 'pending') {
          dispatch(createStripeSourceRequest());
          dispatch(
            bind(
              createStripeSource({
                stripe,
                payment,
                order,
                customer,
              }),
              source => {
                dispatch(createStripeSourceSuccess());
                dispatch(createOrderSuccess());

                if (source.redirect && source.redirect.url) {
                  dispatch(payOrderRequest());
                  dispatch(location(source.redirect.url));
                  return;
                }

                if (source.status && source.status === 'chargeable') {
                  dispatch(payOrderRequest());
                  dispatch(
                    bind(
                      apiPayOrder({ source, order_id }),
                      () => {
                        dispatch(payOrderSuccess());
                        dispatch(
                          updateActiveOrder({ order_id, order_status: 'paid' })
                        );
                        history.push('/order');
                      },
                      () => {
                        dispatch(
                          payOrderFailure({
                            message:
                              'Leider konnten wir Ihre Zahlung nicht verarbeiten, bitte verwenden Sie eine andere Zahlungsmethode. Bei Fragen wenden Sie sich an unsere Auftragsannahme.',
                          })
                        );
                      }
                    )
                  );
                }
              },
              error => {
                dispatch(createStripeSourceFailure({ message: error.message }));
                dispatch(createOrderFailure());

                return;
              }
            )
          );
        } else if (order_status === 'paid') {
          dispatch(createOrderSuccess());
          dispatch(payOrderSuccess());
          history.push('/order');
          return;
        }
      },
      err => {
        dispatch(createOrderFailure(err));
        return;
      }
    )
  );
};



// WEBPACK FOOTER //
// ../shop_main/actions/index.js